import {
  Attribute,
  Condition,
  Duration,
  PriorityLevel,
  RolePriorities,
  Rule,
  TaskTemplate,
  TaskTemplateForm,
  TaskTemplatesPostBody,
} from '@dispatch/shared/types';

export const parseRule = (apiRule: Rule) => {
  const parsedRules = (
    rulesArray: Rule[]
  ): { ifThis: string; operator: string; meta: string }[] => {
    return (rulesArray || []).map((rule: any) => {
      const operator = Object.keys(rule)?.[0];

      if (operator === '@not') {
        const ifThis = rule?.['@not']?.[0]?.['@in_list']?.[0];
        const meta = rule?.['@not']?.[0]?.['@in_list']?.[1];

        return {
          ifThis,
          operator: '@not_in_list',
          meta,
        };
      }
      const ifThis = rule?.[operator]?.[0];
      const meta = rule?.[operator]?.[1];

      return {
        ifThis,
        operator,
        meta,
      };
    });
  };

  const retrievedCondition: string[] =
    apiRule?.conditions && Object.keys(apiRule?.conditions[0]);
  const condition: string = retrievedCondition?.[0];
  const rulesToParse =
    apiRule?.conditions?.[0]?.[`${condition}` as keyof Condition];

  return {
    ruleDescription: apiRule?.description,
    targetTaskGroupId: apiRule?.targetTaskGroupId,
    condition: condition,
    rules: [...parsedRules(rulesToParse)],
  };
};

export const createRule = (
  formData: TaskTemplateForm
): {
  description?: string;
  targetTaskGroupId: string;
  conditions: Partial<Condition>[];
  targetQueue: string;
  defaultQueue: string;
} => {
  const rules: any /* will fix after getting the rest of the bugs out. Having issues typing the new not change */ =
    formData?.rules?.map((rule) => {
      if (rule.operator === '@not_in_list') {
        return {
          [`@not`]: [{ [`@in_list`]: [rule?.ifThis, [...rule['meta']]] }],
        };
      }
      return {
        [`${rule.operator}`]: [rule?.ifThis, [...rule['meta']]],
      };
    });

  return {
    description: formData?.ruleDescription,
    targetTaskGroupId: formData?.targetTaskGroupId,
    /* 
      target queue and default queue should not be required. 
      The api is however requiring it at the moment. This is in place
      in order to allow creation of task template in the meantime. 
    */
    targetQueue: '',
    defaultQueue: '',
    conditions: [
      {
        [`${rules.length > 1 ? formData?.condition : '@or'}`]: [...rules],
      },
    ],
  };
};

export const taskTemplateDefaultValues: TaskTemplateForm = {
  externalId: '',
  id: '',
  name: '',
  createdAt: null,
  deletedAt: null,
  editedAt: null,
  deactivatedAt: null,
  timesUsed: 1,
  description: '',
  rolePriorities: [
    {
      id: '',
      order: 0,
    },
  ],
  sloInMinutes: '15',
  priorityLevel: PriorityLevel.LOW,
  isTravelRequired: true,
  isStateLicenseRequired: true,
  attributes: [],
  ruleDescription: '',
  targetTaskGroupId: '',
  condition: '',
  rules: [
    {
      ifThis: '',
      operator: '',
      meta: [],
    },
  ],
  durations: [
    {
      peril: '',
      duration: Duration.L,
    },
  ],
};

const cleanRolePriorities = (rolePriorities?: RolePriorities[]) => {
  if (!rolePriorities || rolePriorities.length === 0) return null;

  return rolePriorities.filter((rolePriority) => {
    return rolePriority.id;
  });
};

// TaskTemplatesPostBody
export const getValuesForSubmit = (
  values: TaskTemplateForm
): TaskTemplatesPostBody => {
  if (!values) throw new Error('No form values found');
  if (values?.rolePriorities?.length === 1) {
    if (values?.rolePriorities[0]?.id === '') {
      values.rolePriorities = [];
    }
  }

  return {
    name: values?.name,
    externalId: values?.externalId,
    createdAt: values?.createdAt,
    editedAt: values?.editedAt,
    deletedAt: values?.deletedAt,
    timesUsed: values?.timesUsed,
    deactivatedAt: values?.deactivatedAt,
    description: values?.description,
    rolePriorities: cleanRolePriorities(values?.rolePriorities),
    durations: values?.durations,
    sloInMinutes: values?.sloInMinutes || '0',
    priorityLevel: values?.priorityLevel || PriorityLevel.LOW,
    isTravelRequired: values?.isTravelRequired,
    isStateLicenseRequired: values?.isStateLicenseRequired,
    templateProperties: {
      '$msg.assignmentHandling.travel': values?.isTravelRequired,
      '$msg.assignmentHandling.priority': values?.priorityLevel,
      '$msg.assignmentHandling.durationInMinutes': values?.sloInMinutes,
      '$msg.attributes': values?.attributes,
    },
    rule: createRule(values),
  };
};

export const parseApiData = (data: TaskTemplate): TaskTemplateForm => {
  const parseJsonBooleans = (value: string) => {
    try {
      return JSON.parse(value);
    } catch {
      return false;
    }
  };
  return {
    externalId: data?.externalId,
    name: data?.name,
    description: data?.description,
    id: data?.id,
    createdAt: data?.createdAt,
    editedAt: data?.editedAt,
    timesUsed: data?.timesUsed,
    deactivatedAt: data?.deactivatedAt,
    deletedAt: data?.deletedAt,
    rolePriorities: data?.rolePriorities || [
      {
        id: '',
        order: 0,
      },
    ],
    sloInMinutes: `${data?.templateProperties?.['$msg.assignmentHandling.durationInMinutes']}`,
    priorityLevel: data?.templateProperties?.[
      '$msg.assignmentHandling.priority'
    ] as PriorityLevel,
    isTravelRequired: parseJsonBooleans(
      data?.templateProperties?.['$msg.assignmentHandling.travel'] as string
    ),
    attributes: data?.templateProperties?.['$msg.attributes'] as Attribute[],
    isStateLicenseRequired: true,
    ...parseRule(data?.rule),
    durations: data?.durations,
  };
};
